import React from 'react';
import { useTranslation } from 'react-i18next';
import { getCdnUrl } from '../utils/cdnConfig';

const ImagePreview = ({
  file,
  imageUrl,
  label,
  onRemove,
  altText,
  imageId,
  isMainProfilePicture,
  onSetMainProfilePicture,
  showMainProfileButton = false,
  imageIndex
}) => {
  const { t } = useTranslation();

  // Use either file or imageUrl prop
  // Transform imageUrl through CDN if it's not a blob URL
  const src = file || (imageUrl && !imageUrl.startsWith('blob:') ? getCdnUrl(imageUrl) : imageUrl);

  // Debug logging for button visibility
  console.log('ImagePreview debug:', {
    showMainProfileButton,
    isMainProfilePicture,
    onSetMainProfilePicture: !!onSetMainProfilePicture,
    imageId,
    imageIndex,
    buttonShouldShow: showMainProfileButton && !isMainProfilePicture && onSetMainProfilePicture
  });

  if (!src) {
    return null;
  }

  return (
    <div className={`relative group border-2 rounded-lg p-2 flex flex-col items-center transition-all duration-200 ${
      isMainProfilePicture ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-300 hover:border-blue-300'
    }`}>
      <div className="w-full rounded-md bg-gray-100 mb-2 flex items-center justify-center">
        <img
          src={src}
          alt={altText || label || t('imagePreview.previewAlt', 'Preview')}
          className="max-w-full max-h-64 object-contain rounded-md"
        />
      </div>

      {/* Main Profile Picture Badge */}
      {isMainProfilePicture && (
        <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full flex items-center shadow-lg animate-pulse">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {t('imagePreview.mainProfilePicture', 'Main')}
        </div>
      )}

      {label && (
        <div className="text-sm font-semibold text-gray-700 mb-1 text-center">
          {label}
        </div>
      )}

      {/* Set Main Profile Picture Button */}
      {showMainProfileButton && !isMainProfilePicture && onSetMainProfilePicture && (
        <button
          type="button"
          onClick={() => {
            console.log('ImagePreview button clicked!', { imageId, imageIndex });
            onSetMainProfilePicture(imageId, imageIndex);
          }}
          className="w-full mb-2 px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          title={t('imagePreview.setAsMain', 'Set as Main Profile Picture')}
        >
          {t('imagePreview.setAsMain', 'Set as Main')}
        </button>
      )}

      {/* Remove Button */}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-700"
        title={t('imagePreview.remove', 'Remove Image')}
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

export default ImagePreview; 